import { Modal } from "@@/modals/modal";
import { ModalHeader } from "@@/modals/modal-header";
import { useModal } from "@@/modals/use-modal";
import { ButtonTransparent } from "@@/shared/buttons_v2/button-gray";
import { ButtonPrimary } from "@@/shared/buttons_v2/button-primary";
import { Conditional } from "@@/shared/conditional";
import {
    HorizontalDivider,
    HorizontalLine,
    VerticalDivider,
} from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import type { FloatingBoxActions } from "@@/shared/floating-box";
import { FloatingBox } from "@@/shared/floating-box";
import { ForEach } from "@@/shared/for-each";
import { _FormFieldDescription } from "@@/shared/form/_form-field-description";
import type { FormId } from "@@/shared/form/form-and-field-id";
import { useFormId } from "@@/shared/form/form-id.context";
import { useDayPickerDefaults } from "@@/shared/form/shared/use-day-picker-defaults";
import { usePageBreakpoint } from "@@/shared/responsiveness/use-page-breakpoint";
import { TextBox } from "@@/shared/text";
import { FieldTitle } from "@@/shared/text/field-title";
import { ModalTitle } from "@@/shared/text/modal-title";
import { use100vh } from "@@/shared/use-100-vh";
import { useDateTimeFormatter } from "@@/shared/use-date-time-formatter";
import { useUpdateEffect } from "@@/shared/use-update-effect";
import { customThinScrollbarCss } from "@@/styles/themes/custom-thin-scrollbars";
import { useTheme } from "@emotion/react";
import type {
    DeepKeysOf,
    Hour,
    Minute,
    Percentage,
    TimeRange, // Using the type from common
    Translatable,
} from "@towni/common";
import {
    asDate,
    generateId,
    getMinutes,
    hourMinuteFactory,
    hours,
    isoAndUnixFactory,
    joinTranslatables,
    timeRangeFactory, // Using factory from common
    translation,
} from "@towni/common";
import { isEqual, startOfDay } from "date-fns";
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ComponentProps,
} from "react"; // Import ComponentProps
import type { DateRange } from "react-day-picker"; // Keep DateRange
import { useForm3Field } from "./form3.use-form-field"; // Adjust path as needed
// Removed DayPickerRangeProps
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css"; // <-- ENSURE THIS IS LOADING CORRECTLY
import type { ZodSchema } from "zod";

type Value = TimeRange | undefined; // Use TimeRange from common

// --- Reusable Components ---
const DateTimeRangePickerContainer_FlexColumn = FlexColumn;
const DateRangePicker_FlexColumn = FlexColumn;
const TimePickersContainer_FlexRow = FlexRow;
const TimePicker_FlexColumn = FlexColumn;
const Footer_FlexRow = FlexRow;
const HourPicker_FlexColumn = FlexColumn;
const MinutePicker_FlexColumn = FlexColumn;

// --- Reusable Time Picker Header ---
const _TimePickerHeader = (props: { text: Translatable }) => {
    const theme = useTheme();
    return (
        <TextBox
            text={props.text}
            weight="700"
            css={{
                backgroundColor: theme.colors.default.background.asString,
                padding: 5,
                paddingRight: 15,
                paddingLeft: 15,
                position: "sticky",
                borderBottom: `1px solid ${theme.colors.default.border.asString}`,
                top: 0,
                zIndex: 10,
                textAlign: "center",
            }}
        />
    );
};

// --- Reusable Time Selector Component ---
const _TimeSelector = (props: {
    idSuffix: string;
    fieldId: string;
    title: Translatable;
    selectedHour: Hour | undefined;
    selectedMinute: Minute | undefined;
    onHourSelect: (hour: Hour) => void;
    onMinuteSelect: (minute: Minute) => void;
    minuteOptions: readonly Minute[];
}) => {
    const theme = useTheme();
    const {
        idSuffix,
        fieldId,
        title,
        selectedHour,
        selectedMinute,
        onHourSelect,
        onMinuteSelect,
        minuteOptions,
    } = props;

    const hourListRef = useRef<HTMLDivElement | null>(null);
    const minuteListRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (selectedHour !== undefined && hourListRef.current) {
            const hourElement = document.getElementById(
                `hour_${selectedHour}_${fieldId}_${idSuffix}`,
            );
            hourElement?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [selectedHour, fieldId, idSuffix]);

    useEffect(() => {
        if (selectedMinute !== undefined && minuteListRef.current) {
            const minuteElement = document.getElementById(
                `minute_${selectedMinute}_${fieldId}_${idSuffix}`,
            );
            minuteElement?.scrollIntoView({
                behavior: "smooth",
                block: "center",
            });
        }
    }, [selectedMinute, fieldId, idSuffix]);

    return (
        <TimePicker_FlexColumn
            mainAxis="stretch"
            crossAxis="stretch"
            css={{ flex: 1, minWidth: "max-content", overflow: "hidden" }}>
            <_TimePickerHeader text={title} />
            <FlexRow
                mainAxis="stretch"
                crossAxis="stretch"
                css={{ flex: 1, position: "relative", overflow: "hidden" }}>
                <HourPicker_FlexColumn
                    ref={hourListRef}
                    css={[
                        {
                            flex: 1,
                            overflowX: "hidden",
                            overflowY: "auto",
                            borderRight: `1px solid ${theme.colors.default.border.asString}`,
                        },
                        customThinScrollbarCss,
                    ]}>
                    <ForEach itemOf={hours} getKey={hour => hour}>
                        {hour => (
                            <ButtonTransparent
                                id={`hour_${hour}_${fieldId}_${idSuffix}`}
                                padding={{ topBottom: 5, right: 15, left: 15 }}
                                onClick={() => onHourSelect(hour)}>
                                <TextBox
                                    text={hour.toString().padStart(2, "0")}
                                    padding={{ leftRight: 15, topBottom: 5 }}
                                    weight="700"
                                    css={{
                                        backgroundColor:
                                            hour === selectedHour
                                                ? theme.colors.primary.light
                                                      .asString
                                                : undefined,
                                        border:
                                            hour === selectedHour
                                                ? `1px solid ${theme.colors.primary.border.withAlpha(0.5).asString}`
                                                : `1px solid ${theme.colors.transparent.asString}`,
                                        borderRadius: 5,
                                        color:
                                            hour === selectedHour
                                                ? theme.colors.primary
                                                      .background.asString
                                                : undefined,
                                    }}
                                />
                            </ButtonTransparent>
                        )}
                    </ForEach>
                </HourPicker_FlexColumn>
                <MinutePicker_FlexColumn
                    ref={minuteListRef}
                    css={[
                        {
                            flex: 1,
                            overflowX: "hidden",
                            overflowY: "auto",
                            borderRight: `1px solid ${theme.colors.default.border.asString}`,
                        },
                        customThinScrollbarCss,
                    ]}>
                    <ForEach itemOf={minuteOptions} getKey={minute => minute}>
                        {minute => (
                            <ButtonTransparent
                                id={`minute_${minute}_${fieldId}_${idSuffix}`}
                                padding={{ topBottom: 5, right: 15, left: 15 }}
                                onClick={() => onMinuteSelect(minute)}>
                                <TextBox
                                    text={minute.toString().padStart(2, "0")}
                                    padding={{ leftRight: 15, topBottom: 5 }}
                                    weight="700"
                                    css={{
                                        backgroundColor:
                                            minute === selectedMinute
                                                ? theme.colors.primary.light
                                                      .asString
                                                : undefined,
                                        borderRadius: 5,
                                        border:
                                            minute === selectedMinute
                                                ? `1px solid ${theme.colors.primary.border.withAlpha(0.5).asString}`
                                                : `1px solid ${theme.colors.transparent.asString}`,
                                        color:
                                            minute === selectedMinute
                                                ? theme.colors.primary
                                                      .background.asString
                                                : undefined,
                                    }}
                                />
                            </ButtonTransparent>
                        )}
                    </ForEach>
                </MinutePicker_FlexColumn>
            </FlexRow>
        </TimePicker_FlexColumn>
    );
};

// --- Factory Function ---
const createForm3DateTimeRangePickerComponent = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
>() => {
    // --- Define Props for the Form3 Component ---
    type Props<FieldId extends DeepKeysOf<FormData>> = {
        readonly className?: string;
        readonly fieldId: FieldId;
        readonly formId?: FormId;

        /** External props to pass to the react-day-picker DayPicker component */
        readonly datePickerProps?: Omit<
            // Use Omit instead of deprecated type
            ComponentProps<typeof DayPicker>, // Get props from component type
            "selected" | "onSelect" | "mode" // Exclude internal props
        >;
        readonly disabled?: boolean;
        readonly label?: Translatable;
        readonly labelDescription?: Translatable;
        readonly description?: Translatable;
        readonly hideDescriptionAfterInput?: boolean;
        readonly minuteOptionStep?: 1 | 5 | 10 | 15 | 20;
        readonly hideYearAndMonthPicker?: boolean;
    };

    // --- Form3 Component Definition ---
    const Form3DateTimeRangePicker = <
        FieldId extends DeepKeysOf<FormData> = DeepKeysOf<FormData>,
        FieldValue extends Value = Value,
    >(
        props: Props<FieldId>,
    ) => {
        const theme = useTheme();
        const dayPickerDefaults = useDayPickerDefaults(); // <-- VERIFY THIS HOOK'S OUTPUT FOR RANGE STYLES
        const [{ show, hide }, modalId] = useModal(
            "date_time_range_picker_modal__",
        );
        const isSmallScreen = usePageBreakpoint({ when: "SMALL_SCREEN" });
        const { formatDateAndTimeLong, formatDateMedium } =
            useDateTimeFormatter();
        const formIdFromContext = useFormId({ doNotThrow: true });
        const formId = props.formId || formIdFromContext;

        const field = useForm3Field<FormSchema, FormData, FieldId, FieldValue>({
            fieldId: props.fieldId,
        });

        if (!field)
            throw new Error(
                `Field ${props.fieldId.toString()} in form ${formId} not found`,
            );

        const hasErrors = false; //field.errors.length > 0; // Use errors from field hook
        const [pickerIsOpen, setPickerIsOpen] = useState(false);
        const initialFieldValue = field.value;

        const [selectedRange, setSelectedRange] = useState<
            DateRange | undefined
        >(() => {
            if (!initialFieldValue) return undefined;
            const fromDate = asDate(initialFieldValue.start);
            const toDate = asDate(initialFieldValue.end);
            if (!fromDate) return undefined;
            return {
                from: startOfDay(fromDate),
                to: toDate ? startOfDay(toDate) : undefined,
            };
        });

        const [startHour, setStartHour] = useState<Hour | undefined>(() => {
            if (!initialFieldValue?.start) return undefined;
            return hourMinuteFactory({ date: initialFieldValue.start })?.hour;
        });
        const [startMinute, setStartMinute] = useState<Minute | undefined>(
            () => {
                if (!initialFieldValue?.start) return undefined;
                return hourMinuteFactory({ date: initialFieldValue.start })
                    ?.minute;
            },
        );

        const [endHour, setEndHour] = useState<Hour | undefined>(() => {
            if (!initialFieldValue?.end) return undefined;
            return hourMinuteFactory({ date: initialFieldValue.end })?.hour;
        });
        const [endMinute, setEndMinute] = useState<Minute | undefined>(() => {
            if (!initialFieldValue?.end) return undefined;
            return hourMinuteFactory({ date: initialFieldValue.end })?.minute;
        });

        const [result, setResult] = useState<Value | undefined>(
            initialFieldValue === null ? undefined : initialFieldValue,
        );
        const [isRangeValid, setIsRangeValid] = useState<boolean>(true);
        const rangeValidationError = translation({
            sv: "Sluttiden kan inte vara före starttiden",
            en: "End time cannot be before start time",
        });
        const viewHeight = use100vh();

        useUpdateEffect(() => {
            const fromDate = selectedRange?.from;
            const toDate = selectedRange?.to ?? fromDate;

            if (
                !fromDate ||
                !toDate ||
                startHour === undefined ||
                startMinute === undefined ||
                endHour === undefined ||
                endMinute === undefined
            ) {
                setResult(undefined);
                setIsRangeValid(true);
                return;
            }

            const startDate = new Date(fromDate);
            startDate.setHours(startHour);
            startDate.setMinutes(startMinute);
            startDate.setSeconds(0);
            startDate.setMilliseconds(0);
            const startTimestamp = isoAndUnixFactory(startDate);

            const endDate = new Date(toDate);
            endDate.setHours(endHour);
            endDate.setMinutes(endMinute);
            endDate.setSeconds(0);
            endDate.setMilliseconds(0);
            const endTimestamp = isoAndUnixFactory(endDate);

            // Use timeRangeFactory from common
            const newResult: Value = timeRangeFactory(
                startTimestamp,
                endTimestamp,
            );
            const isValid = endTimestamp.unix >= startTimestamp.unix;
            setIsRangeValid(isValid);

            // Compare using start/end from TimeRange
            if (
                result?.start?.iso !== newResult?.start.iso ||
                result?.end?.iso !== newResult?.end.iso
            ) {
                setResult(newResult);
            }
        }, [selectedRange, startHour, startMinute, endHour, endMinute, result]);

        const componentKey = `${formId}_${props.fieldId.toString()}_range`;
        const minuteOptions = useMemo(
            () => getMinutes(props.minuteOptionStep),
            [props.minuteOptionStep],
        );
        const [key, setKey] = useState(generateId);
        const [defaultMonth, _setDefaultMonth] = useState<Date | undefined>(
            selectedRange?.from ??
                (initialFieldValue?.start
                    ? asDate(initialFieldValue.start)
                    : undefined) ??
                new Date(),
        );
        const setDefaultMonth = useCallback((date: Date | undefined) => {
            _setDefaultMonth(date);
            setKey(generateId());
        }, []);

        const timeSelectorFieldId = props.fieldId.toString();

        const startTimeSelector = useMemo(() => {
            return (
                <_TimeSelector
                    key={`start_time` as const}
                    idSuffix="start"
                    fieldId={timeSelectorFieldId}
                    title={translation({ sv: "Starttid", en: "Start Time" })}
                    selectedHour={startHour}
                    selectedMinute={startMinute}
                    onHourSelect={setStartHour}
                    onMinuteSelect={setStartMinute}
                    minuteOptions={minuteOptions}
                />
            );
        }, [timeSelectorFieldId, startHour, startMinute, minuteOptions]);
        const endTimeSelector = useMemo(
            () => (
                <_TimeSelector
                    key={`end_time` as const}
                    idSuffix="end"
                    fieldId={timeSelectorFieldId}
                    title={translation({ sv: "Sluttid", en: "End Time" })}
                    selectedHour={endHour}
                    selectedMinute={endMinute}
                    onHourSelect={setEndHour}
                    onMinuteSelect={setEndMinute}
                    minuteOptions={minuteOptions}
                />
            ),
            [timeSelectorFieldId, endHour, endMinute, minuteOptions],
        );

        const formatRangeForDisplay = useCallback(
            (value: Value): Translatable => {
                if (!value)
                    return translation({
                        sv: "Välj datumintervall och tider",
                        en: "Choose date range and times",
                    });
                // Use start/end from TimeRange
                const fromStr = formatDateAndTimeLong(value.start, {
                    includeYearIfNotCurrent: true,
                });
                const toStr = formatDateAndTimeLong(value.end, {
                    includeYearIfNotCurrent: true,
                });
                const fromDateOnly = startOfDay(asDate(value.start));
                const toDateOnly = startOfDay(asDate(value.end));
                if (isEqual(fromDateOnly, toDateOnly)) {
                    const dateStr = formatDateMedium(value.start, {
                        includeYearIfNotCurrent: true,
                    });
                    const startTimeStr =
                        hourMinuteFactory({ date: value.start })?.formatted ??
                        "";
                    const endTimeStr =
                        hourMinuteFactory({ date: value.end })?.formatted ?? "";
                    return joinTranslatables([
                        dateStr,
                        ` ${startTimeStr} - ${endTimeStr}`,
                    ]);
                } else {
                    return joinTranslatables([fromStr, " - ", toStr]);
                }
            },
            [formatDateAndTimeLong, formatDateMedium],
        );

        const footer = useCallback(
            (actions: FloatingBoxActions) => (
                <Footer_FlexRow
                    fillParentWidth
                    mainAxis="space-between"
                    crossAxis="center">
                    <Conditional when={!isRangeValid}>
                        <TextBox
                            text={rangeValidationError}
                            color="error"
                            size="S"
                        />
                    </Conditional>
                    <HorizontalDivider />
                    <ButtonPrimary
                        disabled={!result || !isRangeValid}
                        onClick={() => {
                            if (result && isRangeValid) {
                                field.setValue(result as FieldValue);
                                actions.close();
                            }
                        }}>
                        <TextBox
                            text={
                                result && isRangeValid
                                    ? joinTranslatables([
                                          translation({
                                              sv: "Välj ",
                                              en: "Choose ",
                                          }),
                                          formatRangeForDisplay(result),
                                      ])
                                    : translation({
                                          sv: "Välj intervall",
                                          en: "Choose range",
                                      })
                            }
                        />
                    </ButtonPrimary>
                </Footer_FlexRow>
            ),
            [
                field,
                result,
                isRangeValid,
                rangeValidationError,
                formatRangeForDisplay,
            ],
        );

        const _datePickerProps = props.datePickerProps;
        const _disabled = props.disabled;
        const _hideYearAndMonthPicker = props.hideYearAndMonthPicker;

        const dateAndRangePickerContent = useCallback(
            (actions: FloatingBoxActions) => (
                <DateTimeRangePickerContainer_FlexColumn
                    key={"dateAndRangePicker"}
                    crossAxis="stretch"
                    css={{
                        backgroundColor: "white",
                        borderRadius: 10,
                        padding: 5 /* other styles */,
                    }}>
                    <FlexRow
                        css={{ flex: 1, maxHeight: 400 }}
                        mainAxis="stretch"
                        crossAxis="stretch">
                        <DateRangePicker_FlexColumn
                            css={{
                                borderRight: `1px solid ${theme.colors.default.border.asString}`,
                            }}>
                            <DayPicker
                                key={key}
                                {...(_datePickerProps ?? {})} // Pass external props
                                mode="range" // Internal prop
                                captionLayout={
                                    _hideYearAndMonthPicker
                                        ? "label"
                                        : "dropdown"
                                } // Internal prop
                                defaultMonth={defaultMonth} // Internal prop
                                disabled={_disabled} // Internal prop (use field state)
                                selected={selectedRange} // Internal prop
                                onSelect={range => {
                                    setSelectedRange(range);
                                    if (range?.from)
                                        setDefaultMonth(range.from);
                                }} // Internal prop
                                onDayBlur={() => field.dirty} // Use method if available
                                // Styling Props (Verify content from hook)
                                css={dayPickerDefaults.css}
                                styles={dayPickerDefaults.styles}
                                modifiersStyles={{
                                    ...dayPickerDefaults.modifiersStyles,
                                    selected: {
                                        backgroundColor:
                                            theme.colors.primary.light.asString,
                                        borderRadius: 0,
                                        borderColor:
                                            theme.colors.primary.border.withAlpha(
                                                0.5,
                                            ).asString,
                                        color: theme.colors.primary.asString,
                                        fontWeight: 400,
                                    },
                                    selectedTodayAndWithinRange: {
                                        backgroundColor:
                                            theme.colors.primary.light.asString,
                                        borderRadius: 0,
                                        borderColor:
                                            theme.colors.primary.border.withAlpha(
                                                0.5,
                                            ).asString,
                                        color: theme.colors.primary.asString,
                                        textDecoration: "underline",
                                        fontWeight: 400,
                                    },
                                    selectedTodayAndStartOrEndOfRange: {
                                        backgroundColor:
                                            theme.colors.primary.asString,
                                        borderRadius: 0,
                                        borderColor:
                                            theme.colors.primary.border.withAlpha(
                                                0.5,
                                            ).asString,
                                        color: theme.colors.primary.asString,
                                        textDecoration: "underline",
                                        fontWeight: 400,
                                    },
                                }}
                                locale={dayPickerDefaults.locale}
                                numberOfMonths={isSmallScreen ? 1 : 2} // Internal prop
                            />
                        </DateRangePicker_FlexColumn>
                        <VerticalDivider />
                        <TimePickersContainer_FlexRow
                            css={{ flex: 1, minWidth: 260 }}
                            mainAxis="stretch"
                            crossAxis="stretch">
                            {startTimeSelector} <VerticalDivider size={1} />
                            {endTimeSelector}
                        </TimePickersContainer_FlexRow>
                    </FlexRow>
                    <Conditional when={!isRangeValid}>
                        <HorizontalLine
                            color="error"
                            size={1}
                            css={{ width: "100%", marginTop: 5 }}
                        />
                        <TextBox
                            text={rangeValidationError}
                            color="error"
                            size="S"
                            padding={{ all: 10 }}
                            css={{ width: "100%", textAlign: "center" }}
                        />
                    </Conditional>
                    <HorizontalLine
                        size={1}
                        css={{ width: "100%", marginTop: isRangeValid ? 0 : 5 }}
                    />
                    <FlexRow fillParentWidth css={{ padding: 10 }}>
                        {footer(actions)}
                    </FlexRow>
                </DateTimeRangePickerContainer_FlexColumn>
            ),
            [
                theme.colors.default.border.asString,
                theme.colors.primary.light.asString,
                theme.colors.primary.border,
                theme.colors.primary.asString,
                key,
                _datePickerProps,
                _hideYearAndMonthPicker,
                defaultMonth,
                _disabled,
                selectedRange,
                dayPickerDefaults.css,
                dayPickerDefaults.styles,
                dayPickerDefaults.modifiersStyles,
                dayPickerDefaults.locale,
                isSmallScreen,
                startTimeSelector,
                endTimeSelector,
                isRangeValid,
                rangeValidationError,
                footer,
                setDefaultMonth,
                field.dirty,
            ],
        );

        const pickerTriggerField = useCallback(
            (actions: FloatingBoxActions) => (
                <FlexRow
                    crossAxis="center"
                    onClick={() => !_disabled && actions.toggle()}
                    css={{
                        cursor: _disabled ? "not-allowed" : "pointer",
                        backgroundColor:
                            theme.colors.textInput.background.asString,
                        borderRadius: theme.radius,
                        border: `1px solid ${hasErrors ? theme.colors.danger.border.asString : theme.colors.textInput.border.asString}`, // Use danger color
                        opacity: _disabled ? 0.5 : 1,
                    }}>
                    <TextBox
                        text={
                            field.value
                                ? formatRangeForDisplay(field.value)
                                : translation({
                                      sv: "Välj intervall",
                                      en: "Choose range",
                                  })
                        } // Use user's placeholder
                        css={{ flex: 1, padding: 15, userSelect: "text" }}
                        color={
                            !field.value
                                ? theme.colors.textInput.placeholder.asString
                                : undefined
                        }
                    />
                </FlexRow>
            ),
            [
                _disabled,
                field.value,
                theme.colors.textInput.background.asString,
                theme.colors.textInput.border.asString,
                theme.colors.textInput.placeholder.asString,
                theme.colors.danger.border.asString,
                theme.radius,
                hasErrors,
                formatRangeForDisplay,
            ],
        );

        const resetInternalStateToFieldValue = useCallback(() => {
            const currentValue = field.value;
            // Use start/end from TimeRange
            const currentFromDate = currentValue?.start
                ? startOfDay(asDate(currentValue.start))
                : undefined;
            const currentToDate = currentValue?.end
                ? startOfDay(asDate(currentValue.end))
                : undefined;
            setSelectedRange(
                currentFromDate
                    ? { from: currentFromDate, to: currentToDate }
                    : undefined,
            );
            const startHm = currentValue?.start
                ? hourMinuteFactory({ date: currentValue.start })
                : undefined;
            setStartHour(startHm?.hour);
            setStartMinute(startHm?.minute);
            const endHm = currentValue?.end
                ? hourMinuteFactory({ date: currentValue.end })
                : undefined;
            setEndHour(endHm?.hour);
            setEndMinute(endHm?.minute);
            setResult(currentValue ?? undefined);
            setIsRangeValid(
                currentValue
                    ? currentValue.end.unix >= currentValue.start.unix
                    : true,
            );
            setDefaultMonth(currentFromDate ?? new Date());
        }, [field.value, setDefaultMonth]);

        const mobileModal = useMemo(
            () => (
                <Modal
                    modalId={modalId}
                    onHide={resetInternalStateToFieldValue}
                    height={
                        viewHeight > 700
                            ? (0.85 as Percentage)
                            : (0.9 as Percentage)
                    }
                    header={
                        <ModalHeader
                            modalId={modalId}
                            bottomBorder
                            topRightButton={
                                result && isRangeValid
                                    ? {
                                          _type: "TOP_RIGHT_ACTION_TEXT",
                                          text: translation({
                                              sv: "Klar",
                                              en: "Ready",
                                          }),
                                          action: () => {
                                              if (result && isRangeValid) {
                                                  field.setValue(
                                                      result as FieldValue,
                                                  );
                                                  hide();
                                              }
                                          },
                                      }
                                    : undefined
                            }
                            title={
                                <ModalTitle
                                    text={translation({
                                        sv: "Välj intervall",
                                        en: "Select Range",
                                    })}
                                />
                            }
                        />
                    }>
                    <FlexColumn
                        css={{ flex: 1, overflowY: "auto", padding: 15 }}>
                        <FieldTitle
                            text={translation({
                                sv: "Välj datum",
                                en: "Select Dates",
                            })}
                            required={field.isRequired}
                        />
                        <VerticalDivider XS />
                        <FlexRow mainAxis="center">
                            <DayPicker
                                key={key + "_mobile"}
                                {...(_datePickerProps ?? {})}
                                mode="range"
                                captionLayout={
                                    _hideYearAndMonthPicker
                                        ? "label"
                                        : "dropdown"
                                }
                                defaultMonth={defaultMonth}
                                disabled={_disabled}
                                selected={selectedRange}
                                onSelect={range => {
                                    setSelectedRange(range);
                                    if (range?.from)
                                        setDefaultMonth(range.from);
                                }}
                                onDayBlur={() => field.dirty}
                                // Styling Props (Verify content from hook)
                                css={dayPickerDefaults.css}
                                styles={dayPickerDefaults.styles}
                                modifiersStyles={{
                                    ...dayPickerDefaults.modifiersStyles,
                                    selected: {
                                        backgroundColor:
                                            theme.colors.primary.light.asString,
                                        borderRadius: 0,
                                        borderColor:
                                            theme.colors.primary.border.withAlpha(
                                                0.5,
                                            ).asString,
                                        color: theme.colors.primary.asString,
                                        fontWeight: 400,
                                    },
                                    selectedTodayAndWithinRange: {
                                        backgroundColor:
                                            theme.colors.primary.light.asString,
                                        borderRadius: 0,
                                        borderColor:
                                            theme.colors.primary.border.withAlpha(
                                                0.5,
                                            ).asString,
                                        color: theme.colors.primary.asString,
                                        textDecoration: "underline",
                                        fontWeight: 400,
                                    },
                                    selectedTodayAndStartOrEndOfRange: {
                                        backgroundColor:
                                            theme.colors.primary.asString,
                                        borderRadius: 0,
                                        borderColor:
                                            theme.colors.primary.border.withAlpha(
                                                0.5,
                                            ).asString,
                                        color: theme.colors.primary.asString,
                                        textDecoration: "underline",
                                        fontWeight: 400,
                                    },
                                }}
                                locale={dayPickerDefaults.locale}
                                numberOfMonths={1}
                            />
                        </FlexRow>
                        <VerticalDivider M />
                        <FlexColumn
                            css={{
                                minHeight: 250,
                                border: `1px solid ${theme.colors.default.border.asString}`,
                                borderRadius: theme.radius,
                                overflow: "hidden",
                            }}>
                            {startTimeSelector}
                        </FlexColumn>
                        <VerticalDivider M />
                        <FlexColumn
                            css={{
                                minHeight: 250,
                                border: `1px solid ${theme.colors.default.border.asString}`,
                                borderRadius: theme.radius,
                                overflow: "hidden",
                            }}>
                            {endTimeSelector}
                        </FlexColumn>
                        <VerticalDivider M />
                        <Conditional when={!isRangeValid}>
                            <TextBox
                                text={rangeValidationError}
                                color="error"
                                size="S"
                                css={{ textAlign: "center", width: "100%" }}
                            />
                            <VerticalDivider M />
                        </Conditional>
                    </FlexColumn>
                    <HorizontalLine size={1} css={{ width: "100%" }} />
                    <FlexRow fillParentWidth css={{ padding: 15 }}>
                        {footer({
                            close: hide,
                            open: show,
                            toggle: () => (pickerIsOpen ? hide() : show()),
                        })}
                    </FlexRow>
                </Modal>
            ),
            [
                modalId,
                resetInternalStateToFieldValue,
                viewHeight,
                result,
                isRangeValid,
                field,
                hide,
                show,
                key,
                _datePickerProps,
                _hideYearAndMonthPicker,
                dayPickerDefaults,
                defaultMonth,
                _disabled,
                selectedRange,
                setDefaultMonth,
                theme,
                startTimeSelector,
                endTimeSelector,
                rangeValidationError,
                footer,
                pickerIsOpen,
            ],
        );

        const picker = useMemo(
            () =>
                isSmallScreen ? (
                    <>
                        {pickerTriggerField({
                            close: hide,
                            open: show,
                            toggle: () => (pickerIsOpen ? hide() : show()),
                        })}
                        {mobileModal}
                    </>
                ) : (
                    <FloatingBox
                        key={componentKey}
                        isOpen={pickerIsOpen}
                        content={dateAndRangePickerContent}
                        trigger={pickerTriggerField}
                        onOpen={() => {
                            resetInternalStateToFieldValue();
                            setPickerIsOpen(true);
                        }}
                        onClose={() => {
                            setPickerIsOpen(false);
                        }}
                    />
                ),
            [
                isSmallScreen,
                pickerTriggerField,
                hide,
                show,
                mobileModal,
                componentKey,
                pickerIsOpen,
                dateAndRangePickerContent,
                resetInternalStateToFieldValue,
            ],
        );

        useEffect(() => {
            if (!isSmallScreen) return;
            if (pickerIsOpen) show();
            else hide();
        }, [isSmallScreen, pickerIsOpen, show, hide]);

        return (
            <FlexColumn
                id={componentKey}
                key={componentKey}
                tag={`form-field_${props.fieldId.toString()}`}
                crossAxis="stretch">
                <Conditional when={!!props.label}>
                    <FlexRow
                        shrink={0}
                        crossAxis="center"
                        mainAxis="space-between">
                        <FieldTitle
                            padding={{ left: 2 }}
                            text={props.label ?? ""}
                            required={field.isRequired}
                        />
                        <Conditional when={!!props.labelDescription}>
                            <HorizontalDivider XXS />
                            <FieldTitle
                                padding={{ left: 2 }}
                                text={props.labelDescription ?? ""}
                                weight="700"
                                size="S"
                                css={{ opacity: 0.5 }}
                            />
                        </Conditional>
                    </FlexRow>
                    <VerticalDivider XS />
                </Conditional>
                {picker}
                <_FormFieldDescription
                    hasErrors={hasErrors || (!isRangeValid && pickerIsOpen)}
                    isDirty={field.dirty} // Use field state if available
                    description={props.description}
                    hideDescriptionAfterInput={
                        !!props.hideDescriptionAfterInput
                    }
                    css={{ marginTop: 5 }}
                />
                {/* <FormErrorMessages errors={field.errors} /> */}
                {/* Display form errors */}
            </FlexColumn>
        );
    }; // End of Form3DateTimeRangePicker component

    return Form3DateTimeRangePicker; // Return the specific component from the factory
}; // End of createForm3DateTimeRangePickerComponent factory

export { createForm3DateTimeRangePickerComponent }; // Export factory and TimeRange type
